import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Stack,
  Typography,
  Button,
  Tooltip,
  Select,
  MenuItem,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
} from '@mui/material';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import dayjs from 'dayjs';
import { RRule, Options } from 'rrule';
import {
  dayMap,
  formattedCurrentDay,
  formatUnitDisplay,
  getWeekday,
  maxDate,
  recurrenceUnits,
  today,
  weekDays,
} from './RecurrenceService';

interface CustomRecurrenceProps {
  onChange: (rrule: string) => void;
  startTime?: string;
}

const CustomRecurrence: React.FC<CustomRecurrenceProps> = ({ onChange, startTime }) => {
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [selectedMonthOption, setSelectedMonthOption] = useState<'day1' | 'currentDay'>('day1');
  const [endDate, setEndDate] = useState(dayjs().add(12, 'month').format('YYYY-MM-DD'));
  const [interval, setInterval] = useState(1);
  const [unit, setUnit] = useState('week');
  const [endType, setEndType] = useState<'never' | 'date' | 'occurrences'>('date');

  const toggleDay = (day: string) => {
    setSelectedDays(prev => (prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]));
  };

  const handleIntervalChange = (value: string) => {
    const newInterval = Math.max(1, Number(value));
    setInterval(newInterval);
    setUnit(prev =>
      newInterval > 1 && !prev.endsWith('s')
        ? prev + 's'
        : newInterval === 1 && prev.endsWith('s')
          ? prev.slice(0, -1)
          : prev
    );
  };

  // Generate RRule based on current settings
  const generateRRule = () => {
    const baseOptions: Partial<Options> = {
      freq: RRule.DAILY,
      interval: interval,
      until: dayjs(maxDate()).toDate(),
      dtstart: startTime ? new Date(startTime) : new Date(dayjs(today()).toISOString()),
    };

    // Set frequency based on unit
    switch (unit.replace('s', '')) {
      case 'day':
        baseOptions.freq = RRule.DAILY;
        break;
      case 'week':
        baseOptions.freq = RRule.WEEKLY;
        if (selectedDays.length > 0) {
          baseOptions.byweekday = selectedDays.map(day => dayMap[day]).filter(Boolean);
        }
        break;
      case 'month':
        baseOptions.freq = RRule.MONTHLY;
        if (selectedMonthOption === 'day1') {
          baseOptions.bymonthday = [1];
        } else {
          baseOptions.byweekday = [getWeekday()];
        }
        break;
      case 'year':
        baseOptions.freq = RRule.YEARLY;
        break;
    }

    // Set end condition
    if (endType === 'date') {
      baseOptions.until = new Date(endDate);
    } else if (endType === 'occurrences') {
      // baseOptions.count = occurrences;
    }

    const rule = new RRule(baseOptions);
    return rule.toString();
  };

  // Call onChange whenever any setting changes
  useEffect(() => {
    onChange(generateRRule());
    // }, [interval, unit, selectedDays, selectedMonthOption, endType, endDate, occurrences]);
  }, [interval, unit, selectedDays, selectedMonthOption, endType, endDate]);

  return (
    <Box mt={2}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography>Repeat every</Typography>
        <TextField
          type="number"
          margin="normal"
          sx={{
            width: 60,
            textAlign: 'center',
            '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
              {
                display: 'none',
              },
            '& input[type=number]': {
              MozAppearance: 'textfield',
              textAlign: 'center',
            },
          }}
          inputProps={{ min: 1 }}
          value={interval}
          onChange={e => {
            const newValue = Number(e.target.value);
            setInterval(newValue);
            handleIntervalChange(e.target.value);
          }}
        />
        <Stack direction="column" spacing={0}>
          <Tooltip title={'Increment'} placement="top">
            <span>
              <FaCaretUp
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  const newValue = interval + 1;
                  setInterval(newValue);
                  handleIntervalChange(newValue.toString());
                }}
              />
            </span>
          </Tooltip>
          <Tooltip title={'Decrement'} placement="bottom">
            <span>
              <FaCaretDown
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  if (interval > 1) {
                    const newValue = interval - 1;
                    setInterval(newValue);
                    handleIntervalChange(newValue.toString());
                  }
                }}
              />
            </span>
          </Tooltip>
        </Stack>

        <Select
          sx={{ width: 120 }}
          value={unit.endsWith('s') ? unit.slice(0, -1) : unit}
          onChange={e => {
            const baseUnit = e.target.value;
            setUnit(interval > 1 ? baseUnit + 's' : baseUnit);
          }}
          renderValue={value => formatUnitDisplay(value, interval)}
        >
          {recurrenceUnits.map(unit => (
            <MenuItem key={unit} value={unit}>
              {unit}
            </MenuItem>
          ))}
        </Select>
      </Stack>

      {(unit === 'week' || unit === 'weeks') && (
        <Stack direction="column" spacing={1}>
          <Typography>Repeat On</Typography>
          <Stack direction="row" spacing={2}>
            {weekDays.map(day => (
              <Tooltip key={day} title={day} arrow placement="top">
                <Button
                  onClick={() => toggleDay(day)}
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    minWidth: 0,
                    fontSize: '16px',
                    fontWeight: 'bold',
                    color: selectedDays.includes(day) ? 'white' : 'primary.main',
                    backgroundColor: selectedDays.includes(day) ? 'primary.main' : 'transparent',
                    border: `2px solid`,
                    borderColor: 'primary.main',
                    '&:hover': {
                      backgroundColor: selectedDays.includes(day) ? 'primary.dark' : 'action.hover',
                    },
                  }}
                >
                  {day.charAt(0)}
                </Button>
              </Tooltip>
            ))}
          </Stack>
        </Stack>
      )}

      {(unit === 'month' || unit === 'months') && (
        <Box mt={2}>
          <Select
            value={selectedMonthOption}
            onChange={e => setSelectedMonthOption(e.target.value as 'day1' | 'currentDay')}
            fullWidth
          >
            <MenuItem value="day1">Monthly on Day 1</MenuItem>
            <MenuItem value="currentDay">Monthly on First {formattedCurrentDay()}</MenuItem>
          </Select>
        </Box>
      )}

      <FormControl component="fieldset" margin="normal">
        <Typography>Ends</Typography>
        <RadioGroup
          value={endType}
          onChange={e => setEndType(e.target.value as 'date' | 'occurrences')}
        >
          <Stack direction="column" spacing={1}>
            <Stack direction="row" spacing={5}>
              <FormControlLabel value="date" control={<Radio />} label="On" />
              <TextField
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                inputProps={{ min: today(), max: maxDate() }}
                fullWidth
                margin="normal"
              />
            </Stack>
            {/**
            <Stack direction="row" spacing={3} alignItems="center">
              <FormControlLabel value="occurrences" control={<Radio />} label="After" />
              <TextField
                type="text"
                value={`${occurrences} occurrences`}
                sx={{
                  width: 150,
                  textAlign: 'center',
                  '& input': { textAlign: 'center', pointerEvents: 'none' },
                }}
                fullWidth
                margin="normal"
                disabled={endType !== 'occurrences'}
              />
              <Stack direction="column" spacing={0}>
                <Tooltip title={'Increment'} placement="top">
                  <span>
                    <FaCaretUp
                      style={{
                        cursor: 'pointer',
                        opacity: endType !== 'occurrences' ? 0.5 : 1,
                      }}
                      onClick={endType !== 'occurrences' ? undefined : handleIncrement}
                    />
                  </span>
                </Tooltip>
                <Tooltip title={'Decrement'} placement="bottom">
                  <span>
                    <FaCaretDown
                      style={{
                        cursor: 'pointer',
                        opacity: endType !== 'occurrences' ? 0.5 : 1,
                      }}
                      onClick={endType !== 'occurrences' ? undefined : handleDecrement}
                    />
                  </span>
                </Tooltip>
              </Stack>
            </Stack> */}
          </Stack>
        </RadioGroup>
      </FormControl>
    </Box>
  );
};

export default CustomRecurrence;
